import { useState, useEffect, useCallback, FormEvent } from "react";

import { DataFilter } from "interfaces";

import { isArray } from "./operator-options";
import validateFilter from "./validate-filter";

const useFilterForm = (
  columnTypes: DataFilter.ColTypes,
  initFilter: DataFilter.Filter | undefined,
  addFilter: (f: DataFilter.Filter) => unknown,
  onColumnSelect?: (col: string) => unknown
) => {
  const [column, setColumn] = useState<string>();
  const [condition, setCondition] = useState<DataFilter.Condition>(
    initFilter?.condition || "include"
  );
  const [operator, setOperator] = useState<DataFilter.Operator>();
  const [range, setRange] = useState<(string | number | Date)[]>(
    initFilter?.range || []
  );

  const [errors, setErrors] = useState<{
    column?: string;
    operator?: string;
    condition?: string;
    range?: string;
  }>({});

  const updateColumn = useCallback(
    async (c: string) => {
      if (c !== column) {
        setColumn(c);
        setErrors({});
        await setRange([]);
      }
      if (onColumnSelect) {
        await onColumnSelect(c);
      }
      if (column) {
        if (
          (columnTypes[c] === "char" || columnTypes[column] === "char") &&
          columnTypes[c] !== columnTypes[column]
        ) {
          setOperator(columnTypes[c] === "char" ? "in" : "between");
        }
      }
    },
    [column, setColumn, setRange, setErrors, onColumnSelect]
  );

  const updateCondition = useCallback(
    (c: DataFilter.Condition) => {
      if (c !== condition) {
        setCondition(c);
        setErrors({});
      }
    },
    [condition, setCondition, setErrors]
  );

  const updateOperator = useCallback(
    (c: DataFilter.Operator) => {
      if (c !== operator) {
        setOperator(c);
        setErrors({});
      }
    },
    [operator, setOperator, setErrors]
  );

  const updateRange = useCallback(
    (range: (string | number | Date)[]) => {
      setRange(range);
      setErrors({});
    },
    [setRange, setErrors]
  );

  const [inited, setInitStatus] = useState(false);

  useEffect(() => {
    setInitStatus(false);
  }, [initFilter, setInitStatus]);

  useEffect(() => {
    if (initFilter && !inited) {
      initFilter.column && updateColumn(initFilter.column);
      initFilter.condition && updateCondition(initFilter.condition);
      initFilter.operator && updateOperator(initFilter.operator);
      initFilter.range && updateRange(initFilter.range);
      setInitStatus(true);
    }
  }, [
    initFilter,
    inited,
    setInitStatus,
    updateColumn,
    updateCondition,
    updateOperator,
    updateRange,
  ]);

  const colType = columnTypes[column || ""];

  const onSubmit = useCallback(
    (e: FormEvent) => {
      e.preventDefault();
      const f = isArray(colType, operator as DataFilter.Operator)
        ? { column, operator, condition, range: range }
        : { column, operator, condition, range: [range[0]] };
      const v = validateFilter(f, colType);
      if (v.success) {
        addFilter(f as DataFilter.Filter);
        setErrors({});
      } else {
        // @ts-ignore
        const err = (v.error as z.ZodError).errors.map((e) => [
          e.path[0],
          e.message,
        ]);
        setErrors(Object.fromEntries(err));
      }
    },
    [column, operator, condition, range, addFilter, column, colType, setErrors]
  );

  return {
    column,
    updateColumn,
    condition,
    updateCondition,
    operator,
    updateOperator,
    range,
    updateRange,
    errors,
    onSubmit,
  };
};

export default useFilterForm;
